import { useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { DataTable } from "../../components/ui/dataTable/DataTable";
import { EmptyState } from "../../components/ui/emptyState/EmptyState";
import { StatusBadge } from "../../components/ui/statusBadge/StatusBadge";
import { LineChart } from "../../components/ui/lineChart/LineChart";
import { MOCK_DATA, useMockDataVersion } from "../../data/mockDataStore";
import { money, date } from "../../utils/format";
import { ESTABLISHMENT_STATUS } from "../../constants/establishmentStatus";
import { useIncidentKpi } from "../tickets/hooks/useIncidents";

export function Dashboard() {
  useMockDataVersion();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const firstName = (keycloak?.tokenParsed?.preferred_username || "").split(/[.@]/)[0] || "";
  const { reservations, etablissements, balances } = MOCK_DATA;
  const { kpi: ticketsKpi, openTotal: ticketsOpen } = useIncidentKpi();
  const ticketsNew = ticketsKpi?.openNb || 0;

  const etabPending = etablissements.filter((e) =>
    [ESTABLISHMENT_STATUS.WAITING_FOR_APPROVAL, ESTABLISHMENT_STATUS.MISSING_INFORMATIONS].includes(e.statut)
  ).length;
  const refundPending = reservations.filter((r) => r.statut === "Remboursement demandé").length;
  const caMonth = 1240000;

  const recentResa = reservations.slice(0, 5);
  const alerts = balances.filter((b) => b.solde < 0);

  const chartVals = [820, 940, 780, 1100, 1250, 1180, 1310, 1420, 1240, 1500, 1620, 1580];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Bonjour{firstName ? ` ${firstName}` : ""} 👋</h1>
          <div className="page-sub">
            Voici un aperçu de l'activité du portail —{" "}
            {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn" data-testid="btn-refresh">
            <i className="fa-solid fa-rotate"></i> Actualiser
          </button>
          <button className="btn btn-primary" onClick={() => navigate("/tickets")} data-testid="btn-see-tickets">
            <i className="fa-solid fa-life-ring"></i> Traiter les tickets
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi" data-testid="kpi-tickets">
          <div className="kpi-label">
            <i className="fa-solid fa-life-ring"></i> Tickets ouverts
          </div>
          <div className="kpi-value">{ticketsOpen}</div>
          <span className="kpi-delta down">
            <i className="fa-solid fa-sparkles"></i> {ticketsNew} nouveaux
          </span>
        </div>
        <div className="kpi" data-testid="kpi-etab">
          <div className="kpi-label">
            <i className="fa-solid fa-building"></i> Établissements à valider
          </div>
          <div className="kpi-value">{etabPending}</div>
          <div className="kpi-foot">2 nouveaux cette semaine</div>
        </div>
        <div className="kpi" data-testid="kpi-refund">
          <div className="kpi-label">
            <i className="fa-solid fa-rotate-left"></i> Remboursements en attente
          </div>
          <div className="kpi-value">{refundPending}</div>
          <span className="kpi-delta neutral">{money(390000)} concernés</span>
        </div>
        <div className="kpi" data-testid="kpi-ca">
          <div className="kpi-label">
            <i className="fa-solid fa-chart-line"></i> CA du mois
          </div>
          <div className="kpi-value">{money(caMonth)}</div>
          <span className="kpi-delta up">
            <i className="fa-solid fa-arrow-up"></i> +12,4%
          </span>
        </div>
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="card-header">
            <div>
              <h3 className="card-title">Évolution du chiffre d'affaires</h3>
              <div className="card-sub">12 derniers mois — en milliers de DA</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-sm" data-testid="chart-1m">1M</button>
              <button className="btn btn-sm" data-testid="chart-3m">3M</button>
              <button
                className="btn btn-sm"
                data-testid="chart-12m"
                style={{ background: "var(--primary-50)", color: "var(--primary)", borderColor: "var(--primary-100)" }}
              >
                12M
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="chart">
              <LineChart values={chartVals} />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Raccourcis</h3>
          </div>
          <div className="card-body" style={{ display: "grid", gap: 8 }}>
            <div className="action-shortcut" onClick={() => navigate("/etablissements")} data-testid="sc-validate-etab">
              <div className="ico">
                <i className="fa-solid fa-clipboard-check"></i>
              </div>
              <div>
                <div className="lbl">Valider un établissement</div>
                <div className="sub">{etabPending} en attente</div>
              </div>
            </div>
            <div className="action-shortcut" onClick={() => navigate("/reservations?f=refund")} data-testid="sc-process-refund">
              <div className="ico">
                <i className="fa-solid fa-rotate-left"></i>
              </div>
              <div>
                <div className="lbl">Traiter un remboursement</div>
                <div className="sub">{refundPending} demandes en attente</div>
              </div>
            </div>
            <div className="action-shortcut" onClick={() => navigate("/tickets?f=new")} data-testid="sc-new-ticket">
              <div className="ico">
                <i className="fa-solid fa-inbox"></i>
              </div>
              <div>
                <div className="lbl">Répondre aux nouveaux tickets</div>
                <div className="sub">{ticketsNew} sans réponse</div>
              </div>
            </div>
            <div className="action-shortcut" onClick={() => navigate("/finance")} data-testid="sc-finance-alert">
              <div className="ico" style={{ background: "var(--danger-50)", color: "var(--danger)" }}>
                <i className="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div>
                <div className="lbl">Alertes financières</div>
                <div className="sub">{alerts.length} partenaires en solde négatif</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: 16 }}></div>

      <div className="grid grid-12">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Réservations récentes</h3>
            <a onClick={() => navigate("/reservations")} style={{ color: "var(--primary)", cursor: "pointer", fontSize: 13, fontWeight: 600 }} data-testid="link-all-resa">
              Tout voir <i className="fa-solid fa-arrow-right"></i>
            </a>
          </div>
          <DataTable
            columns={[
              { label: "Référence", render: (r) => <div><div className="mono strong">{r.id}</div><div className="muted mono">{r.voyageRef}</div></div> },
              { label: "Client", render: (r) => <div><div className="strong">{r.client}</div><div className="muted">{r.voyageurs} voyageur{r.voyageurs > 1 ? "s" : ""}</div></div> },
              { label: "Circuit", render: (r) => <div>{r.circuit}<div className="muted">{r.etab}</div></div> },
              { label: "Départ", render: (r) => date(r.depart) },
              { label: "Montant", render: (r) => <span className="mono strong">{money(r.montant)}</span> },
              { label: "Statut", render: (r) => <StatusBadge statut={r.statut} /> },
            ]}
            rows={recentResa}
            onRowClick={(r) => navigate("/reservations/" + r.id)}
            rowTestIdPrefix="dash-resa"
          />
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Alertes financières</h3>
          </div>
          <div className="card-body" style={{ padding: "12px 16px" }}>
            {alerts.length === 0 ? (
              <EmptyState title="Aucune alerte" sub="Tous les soldes sont sains." />
            ) : (
              alerts.map((b) => (
                <div key={b.etabId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                  <div>
                    <div className="strong">{b.etab}</div>
                    <div className="muted">{b.mouvements} mouvements</div>
                  </div>
                  <div className="balance negative" style={{ padding: "4px 10px" }}>
                    <span className="ico" style={{ background: "var(--danger)", color: "#fff" }}>
                      <i className="fa-solid fa-arrow-down" style={{ fontSize: 10 }}></i>
                    </span>
                    <span className="mono">{money(b.solde)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;
